/**
 * client/wrapper type for the DropDown widget on parent/page
 */
import BaseWidgetClient from '../core/BaseWidgetClient';
import WidgetTypes from '../core/enums/WidgetTypes';
import TempCache from '../core/enums/TempCache';
import DropDownAnimator from '../core/animators/DropDownAnimator';
import DropDownPositioner from '../core/positioners/DropDownPositioner';
import getElementsByClassName from '../core/hooks/getElementsByClassName';
import ConvertPixelToRem from '../core/hooks/ConvertPixelToRem';
import setOpacity from '../core/enums/setOpacity';
import { InvitationDisplayMode, InvitationTabDirection, MobileDisplayMode, Position } from '../core/enums/Enum';
import getViewport from '../core/hooks/getViewport';
import getFixedTop from '../core/hooks/getFixedTop';
import resetCSS from '../core/hooks/resetCSS';
import TempStorageCache from '../core/hooks/TempStorageCache';
import Browser from '../core/hooks/Browser';
import unobserve from '../core/hooks/unobserve';
import observe from '../core/hooks/observe';
import getBody from '../core/hooks/getBody';
import WidgetStates from '../core/hooks/WidgetStates';
import storageAPIExists from '../core/hooks/storageAPIExists';
import showElement from '../core/hooks/showElement';
import createNS from '../core/createNs';

export default function RegisterDropDown() {
    const DropDown = class extends BaseWidgetClient {
        constructor(options) {
            super(
                Object.assign(options, {
                    widget: WidgetTypes.DropDown // containerBg: 'green'
                })
            );
            const me = this;
            me.positioner = new DropDownPositioner(this.options);
            // Object.assign(options, {
            //     containerId: 'apexchat_invitation_container_wrapper'
            // });
            this.options.containerId = 'apexchat_invitation_container_wrapper';
            me.animator = new DropDownAnimator(this.options, this.DOM);
            this.options.height = this.options.invitationHeight;
            this.options.width = this.options.invitationWidth;
            this.options.maskId = 'apexchat_invitation_mask';
            // this.widgetScripts.push('https://cdn.jsdelivr.net/lodash/4/lodash.min.js');
            // this.widgetCss.push('https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css');
            // Set default animation time
            let defaultAnimationTime;

            if (me.hasBeenShown() && this.options.rememberShownState) {
                defaultAnimationTime = 1000;
            } else if (this.options.invitationDisplayMode === InvitationDisplayMode.Drop) {
                defaultAnimationTime = 2000;
            } else if (this.options.invitationDisplayMode === InvitationDisplayMode.Appear) {
                defaultAnimationTime = 0;
            } else {
                defaultAnimationTime = 1000;
            }
            this.options.animationTime = this.options.animationTime || defaultAnimationTime;
            this.mobileInvitation = {
                positionUsed: null,
                originalStyles: null,
                zoom: 1,
                viewZoom: 1,
                lastWindowWidth: 0,
                lastScrollLeft: 0,
                lastScrollTop: 0,
                pinchZoomed: false,
                startTouchTop: -1,
                initialScale: false
            };
            this.tabbedInvitation = {
                invitationTravel: 0,
                travelProperty: 'bottom',
                animatingTab: false,
                borderTopRadius: [
                    'webkitBorderTopRightRadius',
                    'webkitBorderTopLeftRadius',
                    'mozBorderRadiusTopright',
                    'mozBorderRadiusTopleft',
                    'borderTopRightRadius',
                    'borderTopLeftRadius'
                ],
                borderBottomRadius: [
                    'webkitBorderBottomRightRadius',
                    'webkitBorderBottomLeftRadius',
                    'mozBorderRadiusBottomright',
                    'mozBorderRadiusBottomleft',
                    'borderBottomRightRadius',
                    'borderBottomLeftRadius'
                ],
                borderLeftRadius: [
                    'webkitBorderTopLeftRadius',
                    'webkitBorderBottomLeftRadius',
                    'mozBorderRadiusTopleft',
                    'mozBorderRadiusBottomleft',
                    'borderTopLeftRadius',
                    'borderBottomLeftRadius'
                ],
                borderRightRadius: [
                    'webkitBorderTopRightRadius',
                    'webkitBorderBottomRightRadius',
                    'mozBorderRadiusTopright',
                    'mozBorderRadiusBottomright',
                    'borderTopRightRadius',
                    'borderBottomRightRadius'
                ],
                defaultStyles: [
                    '#apexchat_invitation_container_wrapper .tabbed > div > div {',
                    '   -webkit-border-top-right-radius: 0px;',
                    '   -webkit-border-top-left-radius: 0px;',
                    '   -moz-border-radius-topright: 0px;',
                    '   -moz-border-radius-topleft: 0px;',
                    '   border-top-right-radius: 0px;',
                    '   border-top-left-radius: 0px;',
                    '}',
                    '#apexchat_invitation_container_wrapper div.apexchat_invitation_tab {',
                    '   box-shadow: none;',
                    '   -webkit-box-shadow: none;',
                    '   width: 100%;',
                    '   bottom: 100%;',
                    '   text-align: center;',
                    '   position: absolute;',
                    '   margin: 0px;',
                    '   cursor: pointer;',
                    '   padding: 0px;',
                    '}'
                ].join('\n')
            };
            this.ready();
        }

        ready() {
            super.ready();
            const me = this;
            this.subscribe('rounded-dynamic-apply-preserved-scroll-after-minimizing', (args) => {
                // JIRA_5314/5315 - Giving back the previous Scroll position on minimizing RD DOM
                if (me.options.isMobile) {
                    document.body.scrollTop =
                        TempStorageCache.get('preserveBodyScrollPosition') == null
                            ? 0
                            : TempStorageCache.get('preserveBodyScrollPosition');
                }
            });
            this.subscribe('rounded-dynamic-minimized-size', (args) => {
                setTimeout(() => {
                    me.positioner.position({
                        element: me.parentDivId,
                        widgetType: WidgetTypes.DropDown,
                        position: me.options.position,
                        // JIRA-5289 - It was not letting it minimize smoothly.
                        // summary: right and bottom was 15px but 0px is appropriate according to animation.
                        model: { right: '0px', bottom: '0px', overflow: 'visible' },
                        context: document
                    });

                    const iframe = document.getElementById(me.iframeId);
                    const content =
                        iframe.contentDocument.getElementById(me.options.containerId) ||
                        iframe.contentWindow.document.getElementById(me.options.containerId);
                    // content.style.right = '35px'; JIRA-5289 - It was not letting it minimize smoothly.
                    const invitationPosition = me.options.position == 1 ? 'left' : 'right';
                    // fallback default
                    let minimizedOffsetWidth = 85;
                    const minimized =
                        iframe.contentDocument.getElementsByClassName(
                            'apexchat_rd_agent_icon_wrapper'
                        )[0] ||
                        iframe.contentWindow.document.getElementsByClassName(
                            'apexchat_rd_agent_icon_wrapper'
                        )[0];
                    if (minimized && minimized.offsetWidth > 0) {
                        minimizedOffsetWidth = minimized.offsetWidth + 35;
                    }
                    let widgetWidth = minimizedOffsetWidth; // content.offsetWidth;
                    if (invitationPosition == 'right') {
                        widgetWidth += 70;
                    } else {
                        widgetWidth += 50;
                    }
                    const wrapper = document.getElementById(me.parentDivId);
                    // very first time OffsetHeight is correct when RD widget maximized and then minimized in this case the invitation is hidden
                    // if invitation is hidden then offsetHeight appears 0, we are using tempCache to get the rdInvitation Height.
                    if (content.offsetHeight > 0) {
                        TempStorageCache.add('rdInvitationHeight', content.offsetHeight);
                    }
                    const rdInvitationHeight = `${content.offsetHeight > 0
                        ? content.offsetHeight + 10
                        : TempStorageCache.get('rdInvitationHeight') + 10
                        }px`;
                    wrapper.style.width = `${widgetWidth}px`;
                    wrapper.style.height = rdInvitationHeight;

                    iframe.style.width = `${widgetWidth}px`;
                    iframe.style.height = rdInvitationHeight;
                }, 50);
            });

            this.subscribe('dropdown-size', (args) => {
                // in case of rd invite if page refreshes we dont need dropdown-size event to be executed because RD has its own minimizing mechanism
                // if me.isPageRefreshed()) { return; }
                // I commented the above line because that line was added in CHAT-5291 ticket. But I resolve that issues in this ticket..
                setTimeout(() => {
                    me.monitorIE(document.getElementById(me.parentDivId));
                    const iframe = document.getElementById(me.iframeId);
                    const el =
                        iframe.contentDocument.getElementById(args.containerId) ||
                        iframe.contentWindow.document.getElementById(args.containerId); // iframe.contentWindow.
                    if (!el) {
                        return;
                    }
                    let width;
                    let height;
                    if (el.offsetWidth > 0) {
                        me.InvitationSize = { width: el.offsetWidth, height: el.offsetHeight };
                    } else {
                        width =
                            el.offsetWidth || `${me.InvitationSize && me.InvitationSize.width}px`;
                        height =
                            el.offsetHeight || `${me.InvitationSize && me.InvitationSize.height}px`;
                    }
                    showElement(document.getElementById(me.parentDivId));
                    iframe.style.width =
                        width || `${el.offsetWidth + (me.options.isChevronStyleDOM ? 25 : 0)}px`;
                    iframe.style.height =
                        height || `${el.offsetHeight + (me.options.isChevronStyleDOM ? 36 : 0)}px`;
                    me.$container.style.width = width || `${iframe.offsetWidth}px`;
                    me.$container.style.height = height || `${iframe.offsetHeight}px`;
                    let wrapper = null;
                    wrapper = document.getElementById(me.parentDivId);
                    iframe.style.height = ConvertPixelToRem(`${iframe.offsetHeight + 10}px`); // 8092- Convert Pixels to rem for RD
                    wrapper.style.height = ConvertPixelToRem(`${wrapper.offsetHeight + 10}px`);
                    iframe.style.width = ConvertPixelToRem(`${iframe.offsetWidth + 38}px`);
                    wrapper.style.width = ConvertPixelToRem(`${wrapper.offsetWidth + 38}px`);
                    el.style.overflow = '';
                    const position =
                        me.isPageRefreshed() || args.hasBeenShown
                            ? me.options.secondaryPosition
                            : me.options.position;
                    me.positioner.position({
                        element: me.parentDivId,
                        widgetType: WidgetTypes.DropDown,
                        position
                    });
                    me.monitorPosition(position);
                    // 8092- Convert Pixels to rem for RD
                    // below changes is for RD only, when margins set in pixels from position method which was mention above we need to convert it into rem unit
                    wrapper.style.marginBottom = ConvertPixelToRem(wrapper.style.marginBottom);
                    wrapper.style.marginTop = ConvertPixelToRem(wrapper.style.marginTop);
                    wrapper.style.marginRight = ConvertPixelToRem(wrapper.style.marginRight);
                    wrapper.style.marginLeft = ConvertPixelToRem(wrapper.style.marginLeft);
                    if (args.onCreateSizing) {
                        // stop animation when minimized invitation shown after refresh

                        if (me.options.wiggleEnabled) {
                            me.setupWiggling();
                        }
                        // If Masking is enabled then show mask.
                        if (me.options.invitationMask) {
                            // Masking Flag
                            me.showMask();
                        }
                        me.handleMobileEvents();
                    }
                }, 50); // Without timeout it was creating issues while minimizing
            });
            this.subscribe('dropdown-hidemask', (args) => {
                me.hideMask();
            });
            this.subscribe('dropdown-chevronStyle-minimize', (args) => {
                // Turning Off Wiggling once user click
                me.stopWiggling();
                if (args.setSecondaryPosition) {
                    me.positioner.position({
                        element: me.parentDivId,
                        widgetType: WidgetTypes.DropDown,
                        position: me.options.secondaryPosition
                    });
                }
                // if (args.data.IsDropDownMinimized) {
                //    document.getElementById(me.parentDivId).style.bottom = '0px';
                // } else {
                //    document.getElementById(me.parentDivId).style.bottom = '0px';
                // }
            });
            this.subscribe(WidgetTypes.DropDown + WidgetStates.Hidden, (args) => {
                document.getElementById(me.parentDivId).style.display = 'none';
            });
            this.subscribe(WidgetTypes.DropDown + WidgetStates.ShownMaximized, (args) => {
                document.getElementById(me.parentDivId).style.display = 'block';
            });
            this.create();
        }

        showMask() {
            const me = this;
            if (storageAPIExists('livechat_mask_hidden')) {
                return;
            }
            this.state.analytics.track('invitation', 'mask'); // TODO: trackTime
            resetCSS(this.options.maskId);
            const mask = document.createElement('div');
            mask.id = this.options.maskId;
            mask.style.backgroundColor = '#000';
            mask.style.display = 'block';
            const dims = getViewport();
            mask.style.width = `${dims[0]}px`;
            mask.style.height = '100%';
            mask.style.minHeight = '1000px';
            mask.style.position = 'fixed';
            mask.style.zIndex = TempCache.CONTAINER_Z_INDEX - 1; // large z-index, but less than the container
            mask.style.left = 0;
            mask.style.top = 0;
            mask.innerHTML = '&nbsp;';
            setOpacity(mask, 7);
            getBody().appendChild(mask);
            this._previousOverflow = getBody().style.overflow;
            const _sizeMask = this.sizeMask.bind2(this);
            observe(
                window,
                'orientationchange',
                () => {
                    _sizeMask();
                },
                false
            );
            observe(
                window,
                'resize',
                () => {
                    _sizeMask();
                },
                false
            );
            this.state.analytics.track('invitation', 'masked');
        }

        sizeMask() {
            const mask = document.getElementById(this.options.maskId);
            if (mask) {
                const dims = getViewport();
                mask.style.width = `${dims[0]}px`;
                mask.style.height = '100%';
            }
        }

        hideMask() {
            ApexChat.storageAPI.StoreTemp('livechat_mask_hidden', true);
            const mask = document.getElementById(this.options.maskId);
            if (mask) {
                mask.style.display = 'none';
                getBody().style.overflow = this._previousOverflow;
            }
        }

        masked() {
            const mask = document.getElementById(this.options.maskId);
            if (mask) {
                return mask.style.display == 'block';
            }
            return false;
        }

        create() {
            super.create();
            const me = this;
            if (!this.options.width) {
                this.options.width = '100%';
            }
            if (!this.options.height) {
                this.options.height = '100%';
            }
            me.$container.innerHTML = `<iframe  tabindex="0"  class="${me.widget}_frame  ${me.widget}_iframe_custom"  id="${me.iframeId}" width="${this.options.width}" height="${this.options.height}" style="border: none;display: block;overflow-x:hidden; overflow-y:hidden;" marginheight="0" marginwidth="0" frameBorder="0" scrolling="no" horizontalscrolling="no" verticalscrolling="no"  allowtransparency="true" title="Chat Invitation"></iframe>`;
            document.body.appendChild(me.$container);
            me.$container.style.cssText = `position: fixed; z-index:${TempCache.CONTAINER_Z_INDEX}; padding: 0px 0px 0px 0px`;
            this.show();
        }

        show() {
            super.show();

            const isRefreshed = this.isPageRefreshed();
        }

        wiggling(dropdownObj) {
            const me = dropdownObj;
            me.animator.wiggleElement({
                element: me.parentDivId,
                position: me.isPageRefreshed() ? me.options.secondaryPosition : me.options.position
            });
            ApexChat.wigglingTimeouts = setTimeout(
                me.wiggling,
                me.options.wiggleInterval + me.options.animationTime,
                me
            );
        }

        stopWiggling() {
            clearTimeout(ApexChat.wigglingTimeouts);
        }

        handleMobileEvents() {
            const me = this;
            if (
                (me.options.mobileDisplayMode === MobileDisplayMode.FixedToScreen ||
                    me.options.mobileDisplayMode === MobileDisplayMode.Offscreen) &&
                me.options.isMobile
            ) {
                // Update position of invitation on touch events
                getBody().addEventListener('touchstart', (e) => {
                    // Get the posisition used, always use the secondary position if
                    // the invitation is hidden.
                    const displayPosition = me.isInvitationHidden()
                        ? me.options.secondaryPosition
                        : me.mobileInvitation.positionUsed;
                    me.startTouchTop = getFixedTop(
                        displayPosition,
                        document.getElementById(me.parentDivId)
                    );
                });
                getBody().addEventListener('touchend', (e) => {
                    // Update zoom with a forced update that will
                    // ignore any page changes after a little delay
                    // to account for any ios scrolling animations
                    setTimeout(() => {
                        me.handleZoom(true);
                    }, 1000);
                });
                getBody().addEventListener('touchmove', (e) => {
                    me.handleZoom();
                });
                // Start by updating the position once
                setTimeout(() => {
                    me.handleZoom();
                }, 100);
                // next, wait till initial animations complete before doing it again
                // and make sure it continues to run
                let forceMode = true;
                setTimeout(() => {
                    setInterval(() => {
                        me.handleZoom(forceMode);
                        forceMode = false;
                    }, 100);
                }, me.options.animationTime);
            }
        }

        handleZoom(forced) {
            const me = this;
            const container = document.getElementById(me.parentDivId);
            const body = getBody();
            const html = document.documentElement;
            // Actual window dimensions
            const windowHeight = window.innerHeight || html.clientHeight || body.clientHeight;
            const windowWidth = window.innerWidth || html.clientWidth || body.clientWidth;
            const windowAspect = windowWidth / windowHeight;
            // Document dimensions
            const documentWidth = Math.max(
                body.scrollWidth,
                html.scrollWidth,
                body.offsetWidth,
                html.offsetWidth,
                body.clientWidth,
                html.clientWidth
            );
            const documentHeight = Math.max(
                body.scrollHeight,
                html.scrollHeight,
                body.offsetHeight,
                html.offsetHeight,
                body.clientHeight,
                html.clientHeight
            );
            // Scroll offsets
            const scrollTop = window.pageYOffset || html.scrollTop || body.scrollTop;
            const scrollLeft = window.pageXOffset || html.scrollLeft || body.scrollLeft;
            // Get the posisition used, always use the secondary position if
            // the invitation is hidden.
            const displayPosition = me.isInvitationHidden()
                ? me.options.secondaryPosition
                : me.mobileInvitation.positionUsed;
            // If no invites found forget it
            if (!container) {
                return;
            }
            const tab = getElementsByClassName(container, 'livechat_invitation_tab')[0]; // get the tab if there is one
            const isClosed = tab && tab.className.search('closed') > -1;
            // Store original styles of the container
            if (
                !me.mobileInvitation.originalStyles ||
                me.mobileInvitation.originalStyles.container !== container
            ) {
                const containerStyles = window.getComputedStyle(container);
                me.mobileInvitation.originalStyles = {
                    container: container,
                    width: parseInt(containerStyles.width, 10),
                    height: parseInt(containerStyles.height, 10),
                    left: parseInt(containerStyles.left, 10),
                    right: parseInt(containerStyles.right, 10),
                    bottom: parseInt(containerStyles.bottom, 10)
                };
            }
            if (me.tabbedInvitation.animatingTab) {
                return;
            }
            // Only change things if we are zoomed in.
            if (windowWidth < documentWidth) {
                me.mobileInvitation.pinchZoomed = true;
                // todo -- sunaid
                // clearInterval(this.wiggleAnimation);
                // clearInterval(this._wiggleTimer);
                if (
                    forced ||
                    me.mobileInvitation.lastScrollLeft != scrollLeft ||
                    me.mobileInvitation.lastScrollTop != scrollTop ||
                    me.mobileInvitation.lastWindowWidth != windowWidth
                ) {
                    me.mobileInvitation.lastScrollLeft = scrollLeft;
                    me.mobileInvitation.lastScrollTop = scrollTop;
                    me.mobileInvitation.lastWindowWidth = windowWidth;
                    const scaledScreenWidth =
                        windowAspect > 1 // Width of the invite scaled to the current zoom level
                            ? (Math.min(me.mobileInvitation.originalStyles.width, screen.height) /
                                screen.height) *
                            windowWidth
                            : (Math.min(me.mobileInvitation.originalStyles.width, screen.width) /
                                screen.width) *
                            windowWidth;
                    // The zoom based on the size of the scaled width vs the original width of the invite (this is an inverse to counteract the mobile zoom)
                    const zoom = Math.min(
                        scaledScreenWidth / me.mobileInvitation.originalStyles.width,
                        1
                    );
                    // other functions will need to know about zoom
                    me.mobileInvitation.zoom = zoom;
                    me.mobileInvitation.viewZoom = windowWidth / documentWidth;
                    if (me.options.mobileDisplayMode === MobileDisplayMode.FixedToScreen) {
                        // Just always close the invitation on zoom
                        if (me.options.invitationHideShowTabEnabled) {
                            tab.className = tab.className.replace('open', 'closed');
                        }
                        const invitationPosition = me.calculateInvitationPosition(container);
                        // Keep the invitation at the bottom of the screen and maintain the size
                        // Use 3d transform to scale back the mobile zoom
                        container.style.transformStyle = 'preserve-3d';
                        container.style.webkitTransformStyle = 'preserve-3d';
                        container.style.transform = `scale3D(${zoom}, ${zoom}, 1)`;
                        container.style.webkitTransform = `scale3D(${zoom}, ${zoom}, 1)`;
                        container.style.transformOrigin = '50% 50%';
                        container.style.webkitTransformOrigin = '50% 50%';
                        // Position it absolutely at the bottom of the screen
                        container.style.position = 'absolute';
                        container.style.left = `${invitationPosition.left}px`;
                        if (me.options.invitationHideShowTabEnabled) {
                            if (me.options.invitationTabDirection === InvitationTabDirection.Right) {
                                if (container.offsetHeight > 0) {
                                    container.style.left = `${invitationPosition.left -
                                        me.tabbedInvitation.invitationTravel * zoom
                                        }px`;
                                    container.firstChild.style.left = `${me.tabbedInvitation.invitationTravel}px`;
                                }
                            }
                        }
                        container.style.bottom = `${invitationPosition.bottom}px`;
                        container.style.right = 'auto';
                    } else if (me.options.mobileDisplayMode === MobileDisplayMode.Offscreen) {
                        // Using fixed display mode. Calculate the absolute position from the current
                        // position
                        if (container.style.position === 'fixed') {
                            if (me.mobileInvitation.startTouchTop < 0) {
                                if (container.offsetHeight <= 0) {
                                    // The height has been set to 0 due to start up animations
                                    // Restore this height and ignore any animations
                                    // this is only hit on page refresh when the page is already zoomed in
                                    container.style.height = `${me.invitationHeight}px`;
                                }
                                me.mobileInvitation.startTouchTop = getFixedTop(
                                    displayPosition,
                                    container
                                );
                            }
                            container.style.top = me.mobileInvitation.startTouchTop;
                            container.style.position = 'absolute';
                            if (me.options.invitationHideShowTabEnabled) {
                                switch (me.options.invitationTabDirection) {
                                    case InvitationTabDirection.Left:
                                        container.style.left = `${-me.tabbedInvitation
                                            .invitationTravel}px`;
                                        break;
                                    case InvitationTabDirection.Right:
                                        container.style.right = '0px';
                                        container.firstChild.style.left = `${me.tabbedInvitation.invitationTravel}px`;
                                        break;
                                    default:
                                        break;
                                }
                            }
                            // Odd problem
                            // The minimized chat for some reason doesn't have a height when you zoom in
                            // using the fixed to page display mode. This causes it to get cut off at
                            // the bottom of the screen height. Calculating the height will fix this.
                            if (container.offsetHeight <= 0) {
                                const innerHeight = container.firstChild.offsetHeight;
                                container.style.height = `${innerHeight}px`;
                            }
                        }
                    }
                }
            } else {
                // Reset all styles to original
                me.mobileInvitation.pinchZoomed = false;
                if (container.style.position !== 'fixed') {
                    let fixedBottom = me.mobileInvitation.originalStyles.bottom;
                    let offsetRight = 0;
                    let offsetLeft = 0;
                    // Clear the last window width since we want to recalculate scale at regular zoom level
                    me.mobileInvitation.lastWindowWidth = -1;
                    container.style.top = 'auto';
                    container.style.transformStyle = 'none';
                    container.style.webkitTransformStyle = 'none';
                    container.style.transform = 'none';
                    container.style.webkitTransform = 'none';
                    container.firstChild.style.left = 'auto';
                    me.mobileInvitation.zoom = 1;
                    container.style.position = 'fixed';
                    if (
                        me.options.invitationHideShowTabEnabled &&
                        tab.className.search('closed') > -1
                    ) {
                        switch (me.options.invitationTabDirection) {
                            case InvitationTabDirection.Down:
                                fixedBottom = -this.tabbedInvitation.invitationTravel;
                                break;
                            case InvitationTabDirection.Left:
                                offsetLeft = -this.tabbedInvitation.invitationTravel;
                                break;
                            case InvitationTabDirection.Right:
                                offsetRight = -this.tabbedInvitation.invitationTravel;
                                break;
                            default:
                                break;
                        }
                    }
                    container.style.bottom = `${fixedBottom}px`;
                    if (
                        !me.options.invitationHideShowTabEnabled ||
                        this.options.invitationTabDirection === InvitationTabDirection.Down
                    ) {
                        container.style.left = isNaN(this.mobileInvitation.originalStyles.left)
                            ? 'auto'
                            : `${this.mobileInvitation.originalStyles.left + offsetLeft}px`;
                        container.style.right = isNaN(this.mobileInvitation.originalStyles.right)
                            ? 'auto'
                            : `${this.mobileInvitation.originalStyles.right + offsetRight}px`;
                    } else {
                        switch (this.options.invitationTabDirection) {
                            case InvitationTabDirection.Left:
                                container.style.left = `${offsetLeft}px`;
                                container.style.right = 'auto';
                                break;
                            case InvitationTabDirection.Right:
                                container.style.left = 'auto';
                                container.style.right = `${offsetRight}px`;
                                break;
                            default:
                                break;
                        }
                    }
                    // todo - sunaid
                    // this.setupWiggling();
                }
                // Make sure the invite fits within the window when not zoomed as well
                // (This would happen when initial-scale is set in the head)
                // Sets transform origin according to position used to avoid
                // scaling the invite to an offscreen position
                // Should only need to do this when window width changes
                // A forced window width change is generated above if switching from absolute to fixed position
                if (
                    windowWidth < container.offsetWidth &&
                    windowWidth != me.mobileInvitation.lastWindowWidth
                ) {
                    me.mobileInvitation.initialScale = true;
                    const scale = windowWidth / container.offsetWidth;
                    me.mobileInvitation.zoom = scale;
                    // Reset position for scaling
                    container.style.left = '0px';
                    container.style.right = 'auto';
                    const originTop =
                        displayPosition === Position.RightCenter ||
                            displayPosition === Position.LeftCenter ||
                            displayPosition === Position.Center
                            ? '50%' // Middle of screen vertically
                            : '100%'; // Bottom of screen
                    container.style.transformStyle = 'preserve-3d';
                    container.style.webkitTransformStyle = 'preserve-3d';
                    container.style.transform = `scale3D(${scale}, ${scale}, 1)`;
                    container.style.webkitTransform = `scale3D(${scale}, ${scale}, 1)`;
                    container.style.webkitTransformOrigin = `0% ${originTop}`;
                    container.style.transformOrigin = `0% ${originTop}`;
                    // Apply the tabbed position
                    if (tab && tab.className.search('closed') > -1) {
                        if (me.options.invitationTabDirection === InvitationTabDirection.Left) {
                            container.style.left = `${-me.tabbedInvitation.invitationTravel * scale
                                }px`;
                        } else if (
                            me.options.invitationTabDirection === InvitationTabDirection.Right
                        ) {
                            container.style.left = `${me.tabbedInvitation.invitationTravel * scale
                                }px`;
                        } else {
                            container.style.bottom = `${-me.tabbedInvitation.invitationTravel * scale
                                }px`;
                        }
                    }
                    me.mobileInvitation.lastWindowWidth = windowWidth;
                } else if (windowWidth > container.offsetWidth) {
                    // Restore position as we went landscape
                    container.style.transformStyle = 'none';
                    container.style.webkitTransformStyle = 'none';
                    container.style.transform = 'none';
                    container.style.webkitTransform = 'none';
                }
            }
        }

        // Calculates an absolute position for left, and bottom of the invitation based on display modes
        calculateInvitationPosition(container) {
            const me = this;
            let bottomOffset = 0;
            const body = getBody();
            const html = document.documentElement;
            const viewPort = getViewport();
            const windowWidth = viewPort[0];
            const windowHeight = viewPort[1];
            const tab = getElementsByClassName(container, 'livechat_invitation_tab')[0];
            let containerTopScreenRelative;
            let distanceFromBottom;
            const scrollLeft = window.pageXOffset || html.scrollLeft || body.scrollLeft;
            const isClosed = tab && tab.className.search('closed') > -1;
            // Get the posisition used, always use the secondary position if
            // the invitation is hidden.
            const displayPosition = me.isInvitationHidden()
                ? me.options.secondaryPosition
                : me.mobileInvitation.positionUsed;
            if (container.offsetHeight <= 0) {
                container.style.height = `${me.invitationHeight}px`;
            }
            containerTopScreenRelative = container.getBoundingClientRect().top;
            distanceFromBottom = Math.round(
                windowHeight -
                (containerTopScreenRelative + container.offsetHeight * me.mobileInvitation.zoom)
            );
            if (isClosed && me.options.invitationTabDirection === InvitationTabDirection.Down) {
                bottomOffset = -me.tabbedInvitation.invitationTravel * me.mobileInvitation.zoom;
            }
            if (
                displayPosition === Position.Center ||
                displayPosition === Position.LeftCenter ||
                displayPosition === Position.RightCenter
            ) {
                bottomOffset +=
                    (windowHeight - container.offsetHeight * me.mobileInvitation.zoom) / 2;
            }
            const left =
                scrollLeft +
                (container.offsetWidth * me.mobileInvitation.zoom - container.offsetWidth) / 2;
            let leftOffset = 0;
            // Adjust location for specific display position
            if (
                !me.options.invitationHideShowTabEnabled ||
                (me.options.invitationHideShowTabEnabled &&
                    me.options.InvitationTabDirection === InvitationTabDirection.Down)
            ) {
                // Use display position
                switch (displayPosition) {
                    case Position.RightCenter:
                    case Position.Right:
                        leftOffset = windowWidth - container.offsetWidth * me.mobileInvitation.zoom;
                        break;
                    case Position.Center:
                    case Position.Middle:
                        leftOffset =
                            windowWidth - (container.offsetWidth * me.mobileInvitation.zoom) / 2;
                        break;
                    default:
                        break;
                }
            } else {
                // Use tab direction
                switch (me.options.invitationTabDirection) {
                    case InvitationTabDirection.Left:
                        if (isClosed || me.mobileInvitation.pinchZoomed) {
                            leftOffset =
                                -me.tabbedInvitation.invitationTravel * me.mobileInvitation.zoom;
                        }
                        break;
                    case InvitationTabDirection.Right:
                        if (isClosed || me.mobileInvitation.pinchZoomed) {
                            leftOffset =
                                windowWidth -
                                container.offsetWidth * me.mobileInvitation.zoom +
                                me.tabbedInvitation.invitationTravel * me.mobileInvitation.zoom;
                        } else {
                            leftOffset =
                                windowWidth - container.offsetWidth * me.mobileInvitation.zoom;
                        }
                        break;
                    default:
                        break;
                }
            }
            return {
                left: left + leftOffset,
                bottom: bottomOffset + parseInt(container.style.bottom, 10) - distanceFromBottom
            };
        }

        isInvitationHidden() {
            const el = document.getElementById(this.parentDivId);
            if (el) {
                return el.style.display == 'none';
            }
            return false;
        }

        monitorIE(container) {
            if (Browser.isIE()) {
                const me = this;
                window.setInterval(() => {
                    container.style.top = getFixedTop(me.options.position, container);
                }, 50);
            }
        }

        monitorPosition(position) {
            // track it
            const me = this;
            if (this._repositionHandler) {
                unobserve(window, 'orientationchange', this._repositionHandler);
                unobserve(window, 'resize', this._repositionHandler);
            }
            this._repositionHandler = function () {
                me.positioner.position({
                    element: me.parentDivId,
                    widgetType: WidgetTypes.DropDown,
                    position: position
                });
            };
            observe(window, 'orientationchange', this._repositionHandler);
            observe(window, 'resize', this._repositionHandler);
        }
    };
    const fqName = `${WidgetTypes.DropDown}_client`;
    // Export to window
    createNS(fqName, DropDown);
}
